import axios from "axios";
import React from "react";

import { MAX_FALLBACK_BYTES, formatBytes } from "Helpers/Upload";

import UseServices from "Hooks/UseServices";

export const FileContext = React.createContext({
  uploadFile: async () => {},
  getFileExtension: () => {},
});

const FileProvider = ({ children }) => {
  const { api } = UseServices();

  const getFileExtension = (file) => {
    if (!file || !file.name) return "";

    const splittedName = file.name.split(".");

    return splittedName[splittedName.length - 1];
  };

  /**
   * @param {File}   file
   * @param {string} [token]  para telas sem sessão (definição de senha)
   * @param {object} [options]
   * @param {(percent: number) => void} [options.onProgress]  0–100 (D21)
   * @param {AbortSignal}               [options.signal]      cancelar (D21)
   *
   * O fluxo é o que o PDF da parte 2 pede em "Considerações importantes" — e já
   * existia: presigned URL + PUT direto na AWS, entregando ao backend só a URL
   * do objeto depois do sucesso. O que 03/09/2026 acrescentou foi teto de
   * 500 MB (D19/D20), progresso/cancelamento e a mensagem honesta do fallback.
   */
  const uploadFile = async (file, token, options = {}) => {
    if (!file || !file.name) return null;

    const { onProgress, signal } = options;

    try {
      // Método 1: Tentar upload direto ao S3 via presigned URL
      const urlResponse = await api.post(
        "/request-file-upload-url",
        {
          extension: getFileExtension(file),
        },
        token && {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const signedUrl = urlResponse.data.signed_url;
      const location = urlResponse.data.location;

      await axios({
        method: "put",
        url: signedUrl,
        data: file,
        signal,
        headers: {
          "Content-Type": file.type,
          ...(file.type === "application/pdf" && {
            "Content-Disposition": "inline",
          }),
        },
        onUploadProgress: onProgress
          ? (event) => {
              // `total` pode não vir (resposta sem Content-Length): sem ele não
              // há percentual, e chamar onProgress com NaN pintaria a barra
              // errada.
              if (!event.total) return;

              onProgress(Math.round((event.loaded * 100) / event.total));
            }
          : undefined,
      });

      // Upload direto bem-sucedido
      return location;
    } catch (error) {
      // Cancelamento é decisão do usuário, não falha: não faz sentido tentar o
      // fallback nem mostrar erro de upload.
      if (
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError" ||
        signal?.aborted
      )
        throw error;

      // ⚠️ O caminho ALTERNATIVO passa pelo backend, e o multer aceita no
      // máximo MAX_FALLBACK_BYTES. Tentar mesmo assim gasta a banda do usuário
      // duas vezes para terminar num erro que fala de outro limite — a
      // mensagem precisa dizer o que aconteceu.
      if (file.size > MAX_FALLBACK_BYTES) {
        // eslint-disable-next-line no-console
        console.warn(
          "Upload direto ao S3 falhou e o arquivo excede o limite do modo alternativo:",
          error.message,
        );

        throw new Error(
          `O envio direto falhou e este arquivo tem ${formatBytes(
            file.size,
          )} — acima do limite de ${formatBytes(
            MAX_FALLBACK_BYTES,
          )} do modo alternativo. Verifique a conexão e tente novamente.`,
        );
      }
      // eslint-disable-next-line no-console
      console.warn(
        "Upload direto ao S3 falhou, usando fallback via backend:",
        error.message,
      );

      // Método 2: Fallback - Upload via backend
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post("/file-upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.data.success) {
          // eslint-disable-next-line no-console
          console.log(
            "Upload via backend bem-sucedido:",
            response.data.location,
          );
          return response.data.location;
        } else {
          throw new Error(
            response.data.message || "Erro no upload via backend",
          );
        }
      } catch (fallbackError) {
        // eslint-disable-next-line no-console
        console.error("Erro no upload via backend (fallback):", fallbackError);
        throw new Error(
          "Não foi possível fazer upload do arquivo. Tente novamente.",
        );
      }
    }
  };

  const contextValue = {
    uploadFile,
    getFileExtension,
  };

  return (
    <>
      <FileContext.Provider value={contextValue}>
        {children}
      </FileContext.Provider>
    </>
  );
};

export default FileProvider;
